import { useMemo } from "react";
import { useClarifications } from "../../../hooks/useClarifications";
import { useClarificationUnreadCounts } from "../../../hooks/useClarificationUnreadCounts";
import {
  CLARIFICATION_STATUS_LABELS,
  CLARIFICATION_IMPACT_LABELS,
} from "../../../constants/clarification.constants";
import type {
  Clarification,
  ClarificationStatus,
  ClarificationImpact,
} from "../../../types/clarification.type";

interface UseClarificationsDataOptions {
  injectedClarifications?: Clarification[];
  isClientView: boolean;
  rfxId?: number;
  rfxSubmissionId?: number;
  supplierFilter?: number;
}

export const useClarificationsData = ({
  rfxId,
  rfxSubmissionId,
  isClientView,
  injectedClarifications,
  supplierFilter,
}: UseClarificationsDataOptions) => {
  const hook = useClarifications({ rfxId, rfxSubmissionId, isClientView });

  const {
    clarifications,
    clarificationTypes,
    draftClarifications,
    clarificationsWithDraftResponses,
    isLoading,
    addClarification,
    editClarification,
    removeClarification,
    sendDraftClarifications,
    handleExport,
    handleDownloadTemplate,
    handleImport,
    handleValidateImport,
    saveDraftResponse,
    sendSelectedResponses,
    updateLocalClarifications,
    refetch,
  } = hook;

  const mergedClarifications = useMemo(() => {
    if (!injectedClarifications?.length) return clarifications;
    return [...clarifications, ...injectedClarifications];
  }, [clarifications, injectedClarifications]);

  const unreadCounts = useClarificationUnreadCounts(mergedClarifications);

  const selectableItems = useMemo(() => {
    const base = isClientView ? draftClarifications : clarificationsWithDraftResponses;
    if (!injectedClarifications?.length) return base;
    const injectedDrafts = injectedClarifications.filter((c) => !c.is_sent);
    return [...base, ...injectedDrafts];
  }, [isClientView, draftClarifications, clarificationsWithDraftResponses, injectedClarifications]);

  const suppliers = useMemo(() => {
    const uniqueSuppliers = new Map<number, { id: number; name: string }>();
    mergedClarifications.forEach((c) => {
      if (c.rfx_submission_id && c.supplier_name) {
        uniqueSuppliers.set(c.rfx_submission_id, {
          id: c.rfx_submission_id,
          name: c.supplier_name,
        });
      }
    });
    return Array.from(uniqueSuppliers.values());
  }, [mergedClarifications]);

  const availableImpacts = useMemo(() => {
    const uniqueImpacts = new Set<ClarificationImpact | "not_set">();
    let hasNotSet = false;

    mergedClarifications.forEach((c) => {
      if (c.clarification_impact_id) {
        uniqueImpacts.add(c.clarification_impact_id);
      } else {
        hasNotSet = true;
      }
    });

    const impacts: Array<{ label: string; value: ClarificationImpact | "not_set" }> = [];
    Array.from(uniqueImpacts).forEach((impact) => {
      impacts.push({
        value: impact,
        label: CLARIFICATION_IMPACT_LABELS[impact as ClarificationImpact],
      });
    });

    if (hasNotSet) {
      impacts.push({ value: "not_set", label: "Not Set" });
    }

    return impacts;
  }, [mergedClarifications]);

  const availableStatuses = useMemo(() => {
    const uniqueStatuses = new Set<ClarificationStatus>();
    mergedClarifications.forEach((c) => {
      if (c.status) {
        uniqueStatuses.add(c.status);
      }
    });

    const statuses = Array.from(uniqueStatuses)
      .map((status) => ({
        value: status,
        label: CLARIFICATION_STATUS_LABELS[status] || "Unknown",
      }))
      .filter((item) => item.label);

    if (statuses.length === 0) {
      return Object.entries(CLARIFICATION_STATUS_LABELS).map(([value, label]) => ({
        value: Number(value) as ClarificationStatus,
        label,
      }));
    }

    return statuses;
  }, [mergedClarifications]);

  const displayClarifications = useMemo(() => {
    const filtered = supplierFilter
      ? mergedClarifications.filter((c) => c.rfx_submission_id === supplierFilter)
      : mergedClarifications;

    return isClientView ? filtered : filtered.filter((c) => c.is_sent);
  }, [mergedClarifications, supplierFilter, isClientView]);

  return {
    clarifications,
    clarificationTypes,
    isLoading,
    mergedClarifications,
    selectableItems,
    suppliers,
    availableImpacts,
    availableStatuses,
    displayClarifications,
    unreadCounts,
    addClarification,
    editClarification,
    removeClarification,
    sendDraftClarifications,
    handleExport,
    handleDownloadTemplate,
    handleImport,
    handleValidateImport,
    saveDraftResponse,
    sendSelectedResponses,
    updateLocalClarifications,
    refetch,
  };
};

export type ClarificationsDataReturn = ReturnType<typeof useClarificationsData>;
